import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getColorTheme,
  saveColorTheme,
  getCustomThemePalette,
  applyColorThemeToDocument,
  COLOR_THEMES,
} from '@/utils/storage';
import { applyThemeIcon } from '@/utils/themeIcon';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getColorTheme());
  const [customPaletteTick, setCustomPaletteTick] = useState(0);

  const syncFromStorage = useCallback((e) => {
    const fromEvent = e?.detail?.theme && COLOR_THEMES[e.detail.theme] ? e.detail.theme : null;
    if (fromEvent) {
      setTheme(fromEvent);
      return;
    }
    setTheme(getColorTheme());
  }, []);

  useEffect(() => {
    window.addEventListener('rebuildme-color-theme-changed', syncFromStorage);
    return () => window.removeEventListener('rebuildme-color-theme-changed', syncFromStorage);
  }, [syncFromStorage]);

  useEffect(() => {
    const bump = () => setCustomPaletteTick((n) => n + 1);
    window.addEventListener('rebuildme-custom-theme-palette-changed', bump);
    return () => window.removeEventListener('rebuildme-custom-theme-palette-changed', bump);
  }, []);

  useEffect(() => {
    applyColorThemeToDocument(theme);
    void applyThemeIcon(theme);
  }, [theme, customPaletteTick]);

  const changeTheme = (newTheme) => {
    if (newTheme === 'custom') {
      if (!getCustomThemePalette()) return;
    } else if (!COLOR_THEMES[newTheme]) {
      return;
    }
    setTheme(newTheme);
    saveColorTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme, themes: COLOR_THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
